import { Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef, NgZone, ChangeDetectionStrategy } from '@angular/core';
import {
  Router,
  ActivatedRoute,
  NavigationEnd,
  Data,
  RoutesRecognized,
  RouteConfigLoadStart,
  RouteConfigLoadEnd
} from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { defer, forkJoin, from, iif, Subscription } from 'rxjs';
import { Configuration } from './app.constants';
import { EmployeeSettingsService } from './modules/shared/services/employee-settings.service';
import { SignalRService } from './modules/shared/services/signalr.service';
import { Employee } from './modules/shared/models/employee';
import { AuthenticationService } from './modules/shared/services/authentication.service';
import { CachingService } from './modules/shared/services/caching.service';
import { GanttEventsEmitter } from './modules/shared/events/gantt.events';
import { HeaderEventsEmitter } from './modules/shared/events/header.events';
import { WindowEventsEmitter } from './modules/shared/events/window.events';
import { ConfigurationService } from './modules/shared/services/configuration.service';
import { AuthenticationManager } from './modules/shared/managers/authenticationManager';
import { LoginEventsEmitter } from './modules/shared/events/login.events';
import { DropdownEventsEmitter } from './modules/shared/events/dropdown.events';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { AuthService } from './modules/auth/auth.service';
import { MobileAccountPickerService } from './modules/shared/services/mobile-account-picker.service';
import { filter, map, switchMap } from 'rxjs/operators';
import { Account } from './modules/shared/models/account';
import { EmployeeUtil } from './modules/shared/utilities/employee.utilities';
import { OnboardingModalComponent } from './modules/settings/components/common/modals/onboarding/onboarding-modal.component';
import { ModuleService } from './modules/shared/services/module.service';
import { environment } from 'src/environments/environment';
import { NotificationsService } from './modules/shared/services/notifications.service';
import { HeaderBarComponent } from './modules/shared/components/common/app/header-bar/header-bar.component';
import { AllowedFiltersService } from './modules/shared/services/allowed-filters.service';
import { take } from 'rxjs/operators';
import moment from 'moment';
import { TimeZoneService } from './modules/shared/services/timeZone.service';
import { LocalisationService } from './modules/shared/services/localisation.service';
import { Capacitor } from '@capacitor/core';
import { PushNotificationService } from './modules/shared/services/push-notifications.service';
import { UtilitiesService } from './modules/shared/services/utilities.service';
import { AlertController, IonMenu } from '@ionic/angular';
import { MobileVersionUpdateService } from './modules/shared/services/mobile-version-update.service';
import { Network } from '@capacitor/network';
import { ComponentEventEmitter } from './modules/shared/events/component.events';
import { HeaderFiltersCachingService } from './modules/shared/services/header-filters-caching.service';
import { GenerateAccountFiltersService } from './modules/shared/services/generate-account-filters.service';
import { FixedHeaderFiltersService } from './modules/shared/services/fixed-header-filters.service';
import { WtTranslationService } from './modules/wt-translation/wt-translation.service';
import { WtStorageService } from './modules/shared/services/wt-storage.service';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { NavigationService } from './modules/shared/services/navigation.service';
import { PendoService } from './modules/shared/services/pendo.service';
import { UserAgentService } from './modules/shared/services/user-agent.service';
import { BaseConfigurationViewModel } from './modules/shared/viewModels/baseConfigurationViewModel';
import { DatadogService } from './modules/shared/services/datadog.service';
import { syncIonMenu } from './ion-menu-sync';
import { relayWindowEvents } from './window-events-relay';
import { SortingFiltersService } from './modules/shared/services/sorting-filters.service';
import { LayoutStateService } from './modules/shared/services/layout-state.service';
import { POST_ACCOUNT_SWITCH_REDIRECT_KEY } from './modules/shared/constants/mobile-navigation.constants';

@Component({
    selector: 'app-component',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AppComponent implements OnInit {
  private endPageTitle: string = 'WeTrack';
  private defaultPageTitle: string = 'Home';

  @ViewChild('wrapper') wrapper: ElementRef<HTMLElement>;
  @ViewChild('navbar') navBar: HeaderBarComponent;
  @ViewChild('sidebar', { read: ElementRef }) sideBar: ElementRef<HTMLElement>;
  @ViewChild('routeContainer') routeContainer: ElementRef<HTMLElement>;
  @ViewChild('ionMenu', { read: IonMenu }) ionMenu: IonMenu;

  currentAccount: Account;
  currentEmployee: Employee;
  currentVersion: unknown;
  loading: boolean = true;
  isDevMode: boolean;
  bsModalRef: BsModalRef;
  hasBeenInitialized: boolean = false;
  sidebarIsCollapsed: boolean;
  public isNativeDevice: boolean = false;
  public isSidebarToggled = false;
  private menuSubscriptionsInitialised = false;
  private windowEventsRelay: Subscription;
  public hasInternetConnection: boolean = true;
  public maxPageRequestedByComponent: boolean = false;
  public maxHeightRequestedByComponent: boolean = false;
  private mobileConnectionAlert: HTMLIonAlertElement;
  isNotUnauthorizedPage = true;
  private _isPaddingless: boolean;
  private _currentUrl: string;

  constructor(
    private readonly pendoService: PendoService,
    public readonly meta: Meta,
    public readonly router: Router,
    public readonly route: ActivatedRoute,
    public readonly title: Title,
    public readonly authenticationService: AuthenticationService,
    public readonly allowedFiltersService: AllowedFiltersService,
    public readonly generateAccountFiltersService: GenerateAccountFiltersService,
    public readonly cachingService: CachingService,
    private readonly headerFiltersCachingService: HeaderFiltersCachingService,
    private readonly fixedHeaderFiltersService: FixedHeaderFiltersService,
    public readonly ganttEventsEmitter: GanttEventsEmitter,
    public readonly headerEventsEmitter: HeaderEventsEmitter,
    public readonly windowEventsEmitter: WindowEventsEmitter,
    public readonly baseConfigurationService: ConfigurationService,
    public readonly configurations: Configuration,
    public readonly authenticationManager: AuthenticationManager,
    public readonly loginEventEmitter: LoginEventsEmitter,
    public readonly changeDetectorRef: ChangeDetectorRef,
    public readonly dropdownEventsEmitter: DropdownEventsEmitter,
    public readonly bsModalService: BsModalService,
    private readonly authService: AuthService,
    // Instantiating this service wires up the mobile account picker subscription.
    private readonly _mobileAccountPickerService: MobileAccountPickerService,
    private readonly signalRService: SignalRService,
    private readonly modalService: BsModalService,
    private readonly moduleService: ModuleService,
    private readonly notificationsService: NotificationsService,
    private readonly timeZoneService: TimeZoneService,
    private readonly localisationService: LocalisationService,
    private readonly pushNotifications: PushNotificationService,
    private readonly utilitiesService: UtilitiesService,
    private readonly mobileVersionUpdateService: MobileVersionUpdateService,
    private readonly alertCtrl: AlertController,
    private readonly componentEventEmitter: ComponentEventEmitter,
    private readonly wtTranslationService: WtTranslationService,
    private readonly wtStorage: WtStorageService,
    private zone: NgZone,
    private readonly navigationService: NavigationService,
    private readonly userAgentService: UserAgentService,
    private readonly employeeSettingsService: EmployeeSettingsService,
    private readonly datadogService: DatadogService,
    private readonly sortingFiltersService: SortingFiltersService,
    private readonly layoutState: LayoutStateService,
  ) {
    this.currentVersion = require('../../package.json');

    localStorage.setItem('id_token_expires_at', '1');

    this.isDevMode = this.configurations.mode === 'Dev';
    this.subscribeDeeplinking();
  }

  async ngOnInit() {
    this.isNativeDevice = Capacitor.getPlatform() !== 'web';
    this.initIonMenuSubscriptions();
    this.initWindowEventHandlers();

    if (this.isNativeDevice) {
      const networkStatus = await Network.getStatus();
      this.hasInternetConnection = networkStatus.connected;

      if (!this.hasInternetConnection) {
        void this.showConnectivityAlert();
        return;
      }

      this.listenMobileDeviceInternetStatusChange();
    }

    void this.initApplication();

    if (this.isNativeDevice) {
      void App.addListener('backButton', ({ canGoBack }) => {
        if (!canGoBack) {
          void App.exitApp();
        } else {
          window.history.back();
        }
      });
    }
  }

  initApplication() {
    this.authService.init();
    this.initTitleChangeUponNavigation();
    this.moduleService.initModuleChangeUponNavigation();
    this.moduleService.initChangesUponNavigation();

    this.setIosInputFieldFontSize();
    window['GenerateAccountFilters'] = () => {
      this.generateAccountFiltersService.generateFiltersForAccount(this.currentAccount.id).subscribe();
    };

    this.headerEventsEmitter.menuToggled$.subscribe((toggled) => {
      this.sidebarIsCollapsed = !toggled;
    });

    this.bsModalService.onShown.subscribe(() => {
      const modal = document.querySelector('.modal');

      modal.addEventListener('scroll', () => this.dropdownEventsEmitter.broadcastDropdownPositionChanged());
      this.changeDetectorRef.markForCheck();
    });

    let asyncLoadCount = 0;

    this.navigationService.init();
    this.initRouterEvents(asyncLoadCount);

    this.authService.isAuthenticated$.pipe(filter(Boolean)).pipe(take(1)).subscribe((res) => {
      this.initialize()
    });

    if (this.router.url.indexOf('unauthorized') > -1 || this.router.url.indexOf('no-access') > -1) {
      this.loading = false;
    }

    if (this.router.url.indexOf('devtools') > -1) {
      this.loading = false;
    }

    if (this.router.url.indexOf('/external/') > -1 || this.router.url.indexOf('/external-access/') > -1) {
      this.loading = false;
      this.isNotUnauthorizedPage = false;

      setTimeout(() => {
        this.loading = false;
        this.changeDetectorRef.detectChanges();
      }, 0);
    }
  }

  private initRouterEvents(asyncLoadCount: number): void {
    this.router.events
      .pipe(
        filter(event =>
          event instanceof RouteConfigLoadStart ||
          event instanceof RouteConfigLoadEnd ||
          event instanceof NavigationEnd
        )
      )
      .subscribe((event): void => {
        if (event instanceof RouteConfigLoadStart) {
          asyncLoadCount++;
        } else if (event instanceof RouteConfigLoadEnd) {
          asyncLoadCount--;
        } else if (event instanceof NavigationEnd) {
          if (event.url.indexOf('unauthorized') === -1) {
            this.isNotUnauthorizedPage = true;
          }
          if (event.url.indexOf('unauthorized') > -1 || event.url.indexOf('no-access') > -1) {
            this.loading = false;
            this.isNotUnauthorizedPage = false;
          } else if (event.url.indexOf(`/devtools`) > -1) {
            this.loading = false;
          } else if (event.url.indexOf('/external/') > -1 || event.url.indexOf('/external-access/') > -1) {
            this.loading = false;
            this.isNotUnauthorizedPage = false;
            this.changeDetectorRef.detectChanges();
          }
        }

        if (this.hasBeenInitialized || (event instanceof NavigationEnd && (event.url.indexOf('/external/') > -1 || event.url.indexOf('/external-access/') > -1))) {
          if (this.isNativeDevice) {
            this.loading = false;
          } else {
            if (event instanceof NavigationEnd && (event.url.indexOf('/external/') > -1 || event.url.indexOf('/external-access/') > -1)) {
              this.loading = false;
            } else {
              this.loading = !!asyncLoadCount;
            }
          }
        }
      });
  }

  private subscribeDeeplinking() {
    void App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        const domain = environment.DOMAIN;
        const pathArray = event.url.split(domain);
        const appPath = pathArray.pop();
        if (appPath && pathArray.length > 0) {
          void this.router.navigateByUrl(appPath);
        }
      });
    });
  }

  // Wired from ngOnInit, not from the end of the startup chain: that chain waits on several server calls, and on a
  // device where it stalls the header still shows (and flips) the toggle while nothing opens the menu (TLOU-108).
  initIonMenuSubscriptions() {
    if (this.menuSubscriptionsInitialised) {
      return;
    }
    this.menuSubscriptionsInitialised = true;
    this.utilitiesService.sidebarToggled$.subscribe((isToggled) => {
      this.isSidebarToggled = isToggled;
      this.changeDetectorRef.markForCheck();
      void this.syncMenuWithSidebarState(isToggled);
    });
    this.utilitiesService.menuIsOpen = async () => {
      // Inactive while the split pane shows the menu permanently (desktop): the stored state is the truth there.
      if (!this.ionMenu || !(await this.ionMenu.isActive())) {
        return undefined;
      }
      return this.ionMenu.isOpen();
    };
  }

  // The menu is driven by the subject and never pushes state back into it (feeding Ionic's open/close events back
  // into the subject broke the navigation on iOS and Android, TLOU-108).
  private async syncMenuWithSidebarState(isToggled: boolean) {
    // The menu is rendered later than this subscription now starts; "closed" needs no work before it exists.
    if (!this.ionMenu && !isToggled) {
      return;
    }
    await syncIonMenu(this.ionMenu, isToggled);
  }

  get isGanttRoute(): boolean {
    return this.router.url.startsWith(`/${this.currentAccount.accountUID}/gantt`);
  }

  get isEventsDashboardRoute(): boolean {
    return this.router.url.startsWith(`/${this.currentAccount.accountUID}/planning/dashboard`);
  }

  get isIncidentsRoute(): boolean {
    return this.router.url.startsWith(`/${this.currentAccount.accountUID}/control`);
  }
  get isIncidentsReportingPreviewRoute(): boolean {
    return this.router.url.startsWith(`/${this.currentAccount.accountUID}/control/reports`) && this.router.url.endsWith('/view');
  }

  get isSettingsRoute(): boolean {
    return this.router.url.startsWith(`/${this.currentAccount.accountUID}/settings`);
  }

  get isRunsheetsRoute(): boolean {
    return this.router.url.startsWith(`/${this.currentAccount.accountUID}/runsheet`);
  }

  get isReportingRoute(): boolean {
    return this.router.url.indexOf('reports') !== -1;
  }

  get isReportingPreviewRoute(): boolean {
    return this.isReportingRoute && this.router.url.endsWith('view');
  }
  get isPlanningRoute() {
    return this.router.url.startsWith(`/${this.currentAccount.accountUID}/planning`);
  }

  get isMyTrackRoute() {
    return this.router.url.startsWith(`/${this.currentAccount.accountUID}/mytrack`);
  }

  get isPaddingless(): boolean {
    if (this._currentUrl !== this.router.url) {
      this._currentUrl = this.router.url;
      this._isPaddingless = (
        this.isGanttRoute ||
        this.isIncidentsRoute ||
        this.isSettingsRoute ||
        this.isRunsheetsRoute ||
        this.isEventsDashboardRoute ||
        this.isPlanningRoute ||
        this.isReportingRoute ||
        this.isMyTrackRoute
      );
    }

    return this._isPaddingless;
  }

  // Wired from ngOnInit, like the menu (TLOU-108): at the end of the startup chain, any component that threw during
  // the first detectChanges() skipped it, and then no dropdown, tooltip or popup closed anywhere (TLOU-194).
  private initWindowEventHandlers() {
    if (this.windowEventsRelay) {
      return;
    }
    this.windowEventsRelay = relayWindowEvents(this.windowEventsEmitter);
  }

  private initTitleChangeUponNavigation() {
    this.router.events
      .pipe(
        filter((event) => event instanceof RoutesRecognized),
        map((event) => event as RoutesRecognized),
        map((event) => event.state.root.firstChild),
        map((activatedRouteSnapshot) => {
          while (activatedRouteSnapshot.firstChild) {
            activatedRouteSnapshot = activatedRouteSnapshot.firstChild;
          }

          return activatedRouteSnapshot;
        }),
        map((activatedRouteSnapshot) => activatedRouteSnapshot.data)
      )
      .subscribe((data) => {
        this.setProperitesFromRoutingData(data);
      });
  }

  private setProperitesFromRoutingData(event: Data) {
    const title = event['title'] ? `${event['title'] as string} - ${this.endPageTitle}` : `${this.defaultPageTitle} - ${this.endPageTitle}`;

    this.title.setTitle(this.localisationService.localise(title));

    const metaData = event['meta'] as [] || [];
    for (let i = 0; i < metaData.length; i++) {
      this.meta.updateTag(metaData[i]);
    }
  }

  private initialize() {
    this.loading = true;
    this.changeDetectorRef.detectChanges();

    this.authenticationManager.getConfiguration().subscribe((baseConfig: BaseConfigurationViewModel) => {
      this.cachingService.AddEmployeeData(baseConfig.employeeToken, baseConfig.accountData.id);
      this.currentAccount = baseConfig.accountData;
      this.currentEmployee = this.cachingService.GetEmployee(this.currentAccount.id);

      if (!this.currentAccount.isActive) {
        void this.router.navigate([`/${this.currentAccount.accountUID}/unauthorized`]);
        return;
      }

      if (baseConfig.employeeSettings) {
        this.employeeSettingsService.initializeSettings(baseConfig.employeeSettings);
      }

      if (baseConfig.sortingModalMapping) {
        this.sortingFiltersService.initializeSortingModalMappings(baseConfig.sortingModalMapping);
      }

      if (baseConfig.unreadNotificationsCount !== undefined) {
        this.notificationsService.setUnreadCount(baseConfig.unreadNotificationsCount);
      }

      this.timeZoneService.initAdditionalTimeZonesOnAppLoad(baseConfig.accountTimeZones);
      this.moduleService.initModuleItems();
      this.moduleService.initAccountModules();
      this.wtTranslationService.setLanguageAndLocale(this.currentEmployee.languageCode ?? this.wtStorage.getItem('langCode') ?? 'en');

      moment.tz.setDefault(this.timeZoneService.sanitazeTimeZone(baseConfig.accountData.accountSpecificTimeZone));
      this.loginEventEmitter.broadcastCurrentEmployeeChanged(this.currentEmployee);

      from(Promise.resolve()).pipe(
        take(1)
      ).subscribe(() => {
        this.datadogService.initDatadog(baseConfig.datadogApplicationId, baseConfig.datadogClientToken);
        void this.mobileVersionUpdateService.checkForUpdate();
        void this.pushNotifications.initPush(this.currentEmployee.id);
        this.pushNotifications.clearAllPushNotifications();
        this.signalRService.startConnection();
        this.pendoService.init(this.currentEmployee, baseConfig.pendoApiKey);
      });

      this.allowedFiltersService.init().pipe(
        switchMap(() =>
          iif(
            () => !this.currentEmployee.onboarded && !this.currentAccount.isHubAccount,
            defer(() => this.initOnboardingModal()
              .pipe(
                switchMap(() => this.initHeaderAndFiltersService())
              )),
            defer(() => this.initHeaderAndFiltersService())
          )
        )
      ).subscribe(() => {
        this.initTitleChangeUponNavigation();
        this.loading = false;
        this.changeDetectorRef.detectChanges();

        const postSwitchRedirect = localStorage.getItem(POST_ACCOUNT_SWITCH_REDIRECT_KEY);
        if (postSwitchRedirect) {
          localStorage.removeItem(POST_ACCOUNT_SWITCH_REDIRECT_KEY);
          // Validate it's an internal path to guard against stale/corrupted storage values.
          const isSafePath = postSwitchRedirect.startsWith('/');
          // Respect IMS-only accounts even after an account switch.
          if (isSafePath && !this.currentAccount.useIMSOnly) {
            void this.router.navigateByUrl(postSwitchRedirect);
            return;
          }
        }

        if (this.currentAccount.useIMSOnly && this.router.url.indexOf(`${this.currentAccount.accountUID}/control`) < 0) {
          const controlRoute = EmployeeUtil.getControlLandingRoute(this.currentAccount, this.currentEmployee);
          void this.router.navigate([controlRoute ?? `${this.currentAccount.accountUID}/control/list`]);
        }
      });

      this.layoutState.isFullscreen$.subscribe((isFullscreen) => {
        this.maxPageRequestedByComponent = isFullscreen;
        this.changeDetectorRef.markForCheck();
      });

      //TODO: Remove when Hub Gantt is updated to use LayoutStateService!
      this.componentEventEmitter.fullPageSpaceRequested$.subscribe((state: boolean) => {
        if(state !== this.maxPageRequestedByComponent) {
          this.maxPageRequestedByComponent = state;
          this.changeDetectorRef.markForCheck();
        }
      });

      this.componentEventEmitter.hideHeaderRequested$.subscribe((state: boolean) => {
        this.maxHeightRequestedByComponent = state;
        this.changeDetectorRef.markForCheck();
      });
    });
  }

  initHeaderAndFiltersService() {
    return forkJoin([this.headerFiltersCachingService.init(), this.fixedHeaderFiltersService.init()])
  }

  logout(input: boolean) {
    if (input) {
      this.currentEmployee = null;
      this.currentAccount = null;
    }
  }

  private initOnboardingModal() {
      this.loading = false;
      this.changeDetectorRef.markForCheck();
      this.bsModalRef = this.modalService.show(OnboardingModalComponent, {class: 'modal-full-screen'});
      return this.bsModalRef.onHide.pipe(take(1));
  }

  /**
   * The problem: On iOS if the input fields text is less than 16px, when focusing the input that triggers auto zoom.
   *
   * So when using field we should hook the css with "font-size: var(--input-field-font-size)", the variable will be with 14px default font size.
   * On iOS this method will set the variable to be 16px;
   */
  setIosInputFieldFontSize() {
    const iOSArr = ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'];
    if (
      iOSArr.includes(navigator.platform) ||
      // iPad on iOS 13 detection
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    ) {
      document.documentElement.style.setProperty('--input-field-font-size', '16px');
    }
  }

  expandSidebar() {
    this.utilitiesService.broadcastSidebarToggle(true);
  }
  collapseSidebar() {
    this.utilitiesService.broadcastSidebarToggle(false);
  }

  listenMobileDeviceInternetStatusChange() {
    void Network.addListener('networkStatusChange',  (status) => {
      if (status.connected) {
        this.hideConnectivityAlert();
      } else {
        void this.showConnectivityAlert();
      }
    });
  }

  async showConnectivityAlert() {
    this.mobileConnectionAlert = await this.alertCtrl.create({
      header: 'Internet Connectivity',
      message: 'You must have active internet connection to access the application',
      backdropDismiss: false,
      cssClass: 'alert-mobile-status',
    });
    await this.mobileConnectionAlert.present();
    this.changeDetectorRef.markForCheck();
  }

  hideConnectivityAlert() {
    if (this.mobileConnectionAlert) {
      void this.mobileConnectionAlert.dismiss();
    }
  }

  toggleSidebar() {
    this.utilitiesService.broadcastSidebarToggle(!this.isSidebarToggled);
    this.changeDetectorRef.markForCheck();
  }

  get isMobile(): boolean {
    return this.userAgentService.isXSmall;
  }
}
